import * as React from "react";
import {
  Button,
  Divider,
  Field,
  Input,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { FilterChipWrapper, useFilterChipClose, useFilterChipStyles } from "./FilterChipWrapper";

const useStyles = makeStyles({
  presetList: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  presetItem: {
    justifyContent: "flex-start",
    fontWeight: "400",
    borderRadius: "4px",
    minHeight: "32px",
  },
  presetItemSelected: {
    justifyContent: "flex-start",
    fontWeight: "600",
    borderRadius: "4px",
    minHeight: "32px",
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  dateFields: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "12px",
  },
  backLink: {
    justifyContent: "flex-start",
    fontWeight: "400",
    padding: "0",
    minHeight: "24px",
    marginBottom: "8px",
  },
});

export type FormattedDateTimeRange = {
  start: string;
  end: string;
} | null;

/** Raw date range stats from materialization */
export interface DateRangeStats {
  min: string;
  max: string;
  span_days?: number;
}

/**
 * Physical column type for wire-format selection.
 *  - "date"     → emit "YYYY-MM-DD" (no time component)
 *  - "datetime" → emit ISO 8601 UTC (Z), start@T00:00:00.000Z, end@T23:59:59.999Z
 */
export type DateColumnPhysicalType = "date" | "datetime";

export type DateTimeRangeFilterProps = {
  label: string;
  /** Physical column type drives the wire format. Defaults to "datetime". */
  physicalType?: DateColumnPhysicalType;
  /** Stats from materialization — drives presets, defaults, and date-mode classification */
  stats?: DateRangeStats;
  /** Persisted applied value — `{ start: string, end: string }` or `null`. */
  value?: FormattedDateTimeRange;
  /** Whether this filter has been modified from its default */
  isModified?: boolean;
  /** Called when the user clicks the reset icon */
  onReset?: () => void;
  /** Called when the user explicitly selects a preset or custom range. */
  onApply: (value: FormattedDateTimeRange) => void;
  /**
   * Called once on mount with the auto-computed default range.
   * This value is used for chart queries but does NOT mark the filter as "modified".
   * If not provided, falls back to `onApply`.
   */
  onDefaultApplied?: (value: FormattedDateTimeRange) => void;
};

// ── Date-mode classification ────────────────────────────────────────────────

/**
 * Days of slack between `stats.max` and `today` before a dataset is
 * classified as `historical` instead of `live`.
 */
export const STALE_GRACE_DAYS = 7;

const MS_PER_DAY = 86_400_000;

export type DateMode = "live" | "historical" | "future";

export interface DateModeResolution {
  mode: DateMode;
  /**
   * The reference date used to anchor presets ("Last 30 days", etc.).
   * - live      → today
   * - historical → stats.max
   * - future    → stats.max
   */
  presetAnchor: Date;
  /** Upper bound for the date-input `max` attribute — stats.max for non-live, today for live. */
  calendarMax: Date;
  /** Optional subtitle to render under the filter chip label (null when mode === "live"). */
  subtitle: string | null;
}

/**
 * Classify a date column based on its materialized stats relative to `now`.
 *
 *   live        : stats.max ≥ today − graceDays           → anchor = today
 *   historical  : stats.max <  today − graceDays          → anchor = stats.max, "as of <max>"
 *   future      : stats.min >  today                      → anchor = stats.max, "as of <max>"
 *
 * If `stats` is missing, falls back to live mode with `now` as anchor.
 */
export function resolveDateMode(
  stats: DateRangeStats | undefined,
  now: Date,
  graceDays: number = STALE_GRACE_DAYS,
): DateModeResolution {
  if (!stats) {
    return { mode: "live", presetAnchor: now, calendarMax: now, subtitle: null };
  }
  const maxDate = parseDate(stats.max);
  const minDate = parseDate(stats.min);

  if (minDate.getTime() > now.getTime()) {
    return {
      mode: "future",
      presetAnchor: maxDate,
      calendarMax: maxDate,
      subtitle: `as of ${formatDateStr(maxDate)}`,
    };
  }
  const graceMs = graceDays * MS_PER_DAY;
  if (maxDate.getTime() < now.getTime() - graceMs) {
    return {
      mode: "historical",
      presetAnchor: maxDate,
      calendarMax: maxDate,
      subtitle: `as of ${formatDateStr(maxDate)}`,
    };
  }
  return { mode: "live", presetAnchor: now, calendarMax: now, subtitle: null };
}

// ── Preset definition ────────────────────────────────────────────────────────

interface PresetDef {
  key: string;
  label: string;
  /** Days to subtract from the preset anchor for the start. null = custom range */
  days: number | null;
}

/**
 * Build preset list and choose default based on data span.
 * Presets are anchored to the resolved `presetAnchor` (today for live mode,
 * stats.max for historical/future).
 * "All" is NOT included in presets.
 */
function buildPresets(spanDays: number): { presets: PresetDef[]; defaultKey: string } {
  if (spanDays > 730) {
    return {
      defaultKey: "last_12m",
      presets: [
        { key: "last_7d", label: "Last 7 days", days: 7 },
        { key: "last_30d", label: "Last 30 days", days: 30 },
        { key: "last_90d", label: "Last 90 days", days: 90 },
        { key: "last_12m", label: "Last 12 months", days: 365 },
        { key: "ytd", label: "Year to date", days: -1 },
        { key: "custom", label: "Custom Range...", days: null },
      ],
    };
  }
  if (spanDays >= 90) {
    return {
      defaultKey: "last_90d",
      presets: [
        { key: "last_7d", label: "Last 7 days", days: 7 },
        { key: "last_30d", label: "Last 30 days", days: 30 },
        { key: "last_90d", label: "Last 90 days", days: 90 },
        { key: "ytd", label: "Year to date", days: -1 },
        { key: "custom", label: "Custom Range...", days: null },
      ],
    };
  }
  if (spanDays >= 30) {
    return {
      defaultKey: "last_30d",
      presets: [
        { key: "last_7d", label: "Last 7 days", days: 7 },
        { key: "last_30d", label: "Last 30 days", days: 30 },
        { key: "custom", label: "Custom Range...", days: null },
      ],
    };
  }
  return {
    defaultKey: "last_14d",
    presets: [
      { key: "last_3d", label: "Last 3 days", days: 3 },
      { key: "last_7d", label: "Last 7 days", days: 7 },
      { key: "last_14d", label: "Last 14 days", days: 14 },
      { key: "custom", label: "Custom Range...", days: null },
    ],
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function parseDate(s: string): Date {
  return new Date(s.replace(" ", "T"));
}

function subtractDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
}

function yearStart(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Format a Date for the wire when the underlying column is a TIMESTAMP/DATETIME.
 * Snaps to UTC start-of-day or end-of-day so we never inject local-time offsets.
 */
function formatDateTimeIsoUtc(date: Date, edge: "start" | "end"): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const time = edge === "start" ? "00:00:00.000Z" : "23:59:59.999Z";
  return `${y}-${m}-${d}T${time}`;
}

function formatForInput(date: Date | null): string {
  if (!date) return "";
  return formatDateStr(date);
}

export function computeRange(
  preset: PresetDef,
  presetAnchor: Date,
  minDate: Date,
): { start: Date; end: Date } | null {
  if (preset.days === null) return null;
  if (preset.days === -1) {
    return { start: yearStart(presetAnchor), end: presetAnchor };
  }
  const start = subtractDays(presetAnchor, preset.days);
  return { start: start < minDate ? minDate : start, end: presetAnchor };
}

// ── Inner content (rendered inside FilterChipWrapper popover) ────────────────

const DateTimeRangeContent: React.FC<{
  presets: PresetDef[];
  selectedKey: string;
  minDate: Date;
  calendarMax: Date;
  /** Previously applied custom range — used to restore selection on reopen */
  persistedCustomStart?: Date | null;
  persistedCustomEnd?: Date | null;
  onPresetApply: (preset: PresetDef) => void;
  onCustomApply: (start: Date, end: Date) => void;
}> = ({ presets, selectedKey, minDate, calendarMax, persistedCustomStart, persistedCustomEnd, onPresetApply, onCustomApply }) => {
  const styles = useStyles();
  const chipStyles = useFilterChipStyles();
  const closePopover = useFilterChipClose();

  // If reopening with a custom selection, go straight to custom view
  const [view, setView] = React.useState<"presets" | "custom">(
    selectedKey === "custom" ? "custom" : "presets",
  );
  const [customStart, setCustomStart] = React.useState<Date | null>(
    persistedCustomStart ?? minDate,
  );
  const [customEnd, setCustomEnd] = React.useState<Date | null>(
    persistedCustomEnd ?? calendarMax,
  );

  const handlePresetClick = (preset: PresetDef) => {
    if (preset.days === null) {
      // Switch to custom range view — restore persisted values if available
      setCustomStart(persistedCustomStart ?? minDate);
      setCustomEnd(persistedCustomEnd ?? calendarMax);
      setView("custom");
      return;
    }
    onPresetApply(preset);
    closePopover();
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      onCustomApply(customStart, customEnd);
      closePopover();
    }
  };

  const handleBackToPresets = () => {
    setView("presets");
  };

  if (view === "custom") {
    return (
      <>
        <Button
          appearance="transparent"
          size="small"
          className={styles.backLink}
          onClick={handleBackToPresets}
        >
          ← Back to presets
        </Button>

        <div className={styles.dateFields}>
          <Field label="Start Date">
            <Input
              type="date"
              value={formatForInput(customStart)}
              min={formatForInput(minDate)}
              max={formatForInput(calendarMax)}
              onChange={(e) => {
                const d = e.target.value ? new Date(e.target.value) : null;
                setCustomStart(d);
              }}
            />
          </Field>
          <Field label="End Date">
            <Input
              type="date"
              value={formatForInput(customEnd)}
              min={formatForInput(customStart)}
              max={formatForInput(calendarMax)}
              onChange={(e) => {
                const d = e.target.value ? new Date(e.target.value) : null;
                setCustomEnd(d);
              }}
            />
          </Field>
        </div>

        <Divider />
        <div className={chipStyles.footer}>
          <Button
            appearance="primary"
            onClick={handleCustomApply}
            disabled={!customStart || !customEnd}
          >
            Apply
          </Button>
          <Button onClick={handleBackToPresets}>Cancel</Button>
        </div>
      </>
    );
  }

  return (
    <div className={styles.presetList}>
      {presets.map((preset) => (
        <Button
          key={preset.key}
          appearance="subtle"
          className={
            preset.key === selectedKey ? styles.presetItemSelected : styles.presetItem
          }
          onClick={() => handlePresetClick(preset)}
        >
          {preset.label}
        </Button>
      ))}
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Try to match a persisted date range against known presets.
 * Returns the matching preset key, or "custom" if no match.
 */
function matchPreset(
  presets: PresetDef[],
  persistedStart: string,
  persistedEnd: string,
  presetAnchor: Date,
  minDate: Date,
  formatStartFn: (d: Date) => string,
  formatEndFn: (d: Date) => string,
): string {
  for (const preset of presets) {
    if (preset.days === null) continue; // skip "Custom Range..."
    const range = computeRange(preset, presetAnchor, minDate);
    if (!range) continue;
    if (formatStartFn(range.start) === persistedStart && formatEndFn(range.end) === persistedEnd) {
      return preset.key;
    }
  }
  return "custom";
}

export const DateTimeRangeFilter: React.FC<DateTimeRangeFilterProps> = ({
  label,
  physicalType = "datetime",
  stats,
  value: persistedValue,
  isModified = false,
  onReset,
  onApply,
  onDefaultApplied,
}) => {

  const minDate = stats ? parseDate(stats.min) : new Date(0);
  const maxDate = stats ? parseDate(stats.max) : new Date();
  const spanDays =
    stats?.span_days ?? Math.ceil((maxDate.getTime() - minDate.getTime()) / MS_PER_DAY);

  // Classify the date column once on mount; this drives anchoring + subtitle.
  // Using `useMemo` (not state) is fine — `stats` is stable for the lifetime
  // of the dashboard view, and we want the anchor to track wall-clock if the
  // user keeps the page open across day boundaries (re-rendered on parent updates).
  const { presetAnchor, calendarMax, subtitle } = React.useMemo(
    () => resolveDateMode(stats, new Date()),
    [stats],
  );

  const { presets, defaultKey } = React.useMemo(() => buildPresets(spanDays), [spanDays]);

  const formatForQuery = React.useCallback(
    (date: Date, edge: "start" | "end"): string => {
      if (physicalType === "date") {
        return formatDateStr(date);
      }
      return formatDateTimeIsoUtc(date, edge);
    },
    [physicalType],
  );

  // Determine initial selected key: restore from persisted value or use default
  const initialKey = React.useMemo(() => {
    if (persistedValue) {
      return matchPreset(
        presets,
        persistedValue.start,
        persistedValue.end,
        presetAnchor,
        minDate,
        (d) => formatForQuery(d, "start"),
        (d) => formatForQuery(d, "end"),
      );
    }
    return defaultKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedKey, setSelectedKey] = React.useState<string>(initialKey);

  // Apply default on mount — only if no persisted value.
  // Uses `onDefaultApplied` so the value is available for chart queries
  // without marking the filter as "modified" in the UI.
  const defaultApplied = React.useRef(false);
  React.useEffect(() => {
    if (defaultApplied.current) return;
    defaultApplied.current = true;

    // If we have a persisted value, don't overwrite it with default
    if (persistedValue) return;

    const defaultPreset = presets.find((p) => p.key === defaultKey);
    if (!defaultPreset) return;

    const range = computeRange(defaultPreset, presetAnchor, minDate);
    if (range) {
      const value = {
        start: formatForQuery(range.start, "start"),
        end: formatForQuery(range.end, "end"),
      };
      // Route through onDefaultApplied (doesn't mark as modified) if available
      (onDefaultApplied ?? onApply)(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePresetApply = React.useCallback(
    (preset: PresetDef) => {
      setSelectedKey(preset.key);
      const range = computeRange(preset, presetAnchor, minDate);
      if (range) {
        onApply({
          start: formatForQuery(range.start, "start"),
          end: formatForQuery(range.end, "end"),
        });
      }
    },
    [presetAnchor, minDate, formatForQuery, onApply],
  );

  const handleCustomApply = React.useCallback(
    (start: Date, end: Date) => {
      setSelectedKey("custom");
      onApply({
        start: formatForQuery(start, "start"),
        end: formatForQuery(end, "end"),
      });
    },
    [formatForQuery, onApply],
  );

  const handleReset = React.useCallback(() => {
    // Reset visual state to the default preset.
    // Don't call onApply — the default range is already stored in
    // defaultFilterValuesRef (via onDefaultApplied on mount) and will
    // be picked up automatically once filterValues removes the override.
    setSelectedKey(defaultKey);
    onReset?.();
  }, [defaultKey, onReset]);

  // Build display value for the chip
  const displayValue = React.useMemo(() => {
    const preset = presets.find((p) => p.key === selectedKey);
    if (preset && preset.days !== null) return preset.label;
    // Custom range or unknown — selectedKey is "custom"
    return "Custom Range";
  }, [presets, selectedKey]);

  // No-op handlers — the presets view applies instantly, custom view has its
  // own Apply/Cancel rendered inside the content.
  const noop = React.useCallback(() => {}, []);

  return (
    <FilterChipWrapper
      label={label}
      displayValue={displayValue}
      subtitle={subtitle ?? undefined}
      isModified={isModified}
      onReset={handleReset}
      isDirty={false}
      onApply={noop}
      onCancel={noop}
      hideFooter
      surfaceWidth="240px"
    >
      <DateTimeRangeContent
        presets={presets}
        selectedKey={selectedKey}
        minDate={minDate}
        calendarMax={calendarMax}
        persistedCustomStart={
          selectedKey === "custom" && persistedValue
            ? parseDate(persistedValue.start)
            : null
        }
        persistedCustomEnd={
          selectedKey === "custom" && persistedValue
            ? parseDate(persistedValue.end)
            : null
        }
        onPresetApply={handlePresetApply}
        onCustomApply={handleCustomApply}
      />
    </FilterChipWrapper>
  );
};